import { useRef } from "react";
import { motion, useMotionTemplate, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { EASE, scrollToId } from "./Reveal";
import { IMAGES } from "@/lib/images";

const LINES = [
  { text: "A NEW", cls: "" },
  { text: "STANDARD", cls: "text-outline" },
  { text: "IN EVERY", cls: "" },
  { text: "STROKE.", cls: "text-accent" },
];

export default function HeroSection() {
  const cardRef = useRef(null);
  const rotX = useMotionValue(0);
  const rotY = useMotionValue(0);
  const glowX = useMotionValue(50);
  const glowY = useMotionValue(50);
  const sRotX = useSpring(rotX, { stiffness: 120, damping: 16 });
  const sRotY = useSpring(rotY, { stiffness: 120, damping: 16 });
  const shine = useMotionTemplate`radial-gradient(circle at ${glowX}% ${glowY}%, rgba(255,255,255,0.22), transparent 55%)`;

  const handleMove = (e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    rotY.set(px * 16);
    rotX.set(-py * 16);
    glowX.set((px + 0.5) * 100);
    glowY.set((py + 0.5) * 100);
  };

  const handleLeave = () => {
    rotX.set(0);
    rotY.set(0);
    glowX.set(50);
    glowY.set(50);
  };

  return (
    <section className="relative flex min-h-screen flex-col pt-16" data-testid="hero-section">
      <div className="mx-auto grid w-full max-w-7xl flex-1 grid-cols-1 items-center gap-12 px-4 py-14 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground"
          >
            Strovyn Painting Co. — Adelaide, Est. 2026
          </motion.p>

          <h1 className="mt-6 font-serif text-6xl uppercase leading-[0.95] tracking-tight sm:text-8xl lg:text-[7.2rem]">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.35 + i * 0.13, duration: 1, ease: EASE }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <div className="mt-10 flex max-w-xl flex-wrap items-end justify-between gap-8">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.9, ease: EASE }}
              className="max-w-xs text-sm leading-relaxed text-muted-foreground"
            >
              Premium residential and commercial painting. Precision prep,
              flawless finish, and colour treated as craft — not commodity.
            </motion.p>
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
              data-testid="hero-cta-quote-button"
              onClick={() => scrollToId("contact")}
              className="group flex items-center gap-3 bg-accent px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:bg-foreground hover:text-background"
            >
              Start a Project
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </motion.button>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.9, duration: 1.1, ease: EASE }}
          className="lg:col-span-5"
          style={{ perspective: 1200 }}
          ref={cardRef}
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
          data-testid="hero-motion-card"
        >
          <motion.div
            style={{ rotateX: sRotX, rotateY: sRotY, transformStyle: "preserve-3d" }}
            className="relative aspect-square cursor-crosshair"
          >
            <img
              src={IMAGES.marble}
              alt="Fluid crimson paint marbling — tilts in 3D as your cursor moves"
              className="h-full w-full object-cover"
              loading="eager"
              style={{ transform: "translateZ(24px) scale(1.06)" }}
            />
            <motion.div
              className="pointer-events-none absolute inset-0"
              style={{ background: shine, transform: "translateZ(46px)" }}
            />
            <p
              className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em] text-white/85"
              style={{ transform: "translateZ(60px)" }}
            >
              A new standard in innovation
            </p>
            <p
              className="absolute bottom-3 right-4 font-serif text-xl uppercase text-white"
              style={{ transform: "translateZ(60px)" }}
            >
              N°01
            </p>
          </motion.div>
        </motion.div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 pb-8 sm:px-8">
        <button
          onClick={() => scrollToId("services")}
          className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground transition-colors hover:text-accent"
          data-testid="hero-scroll-cue"
        >
          <ArrowDown className="h-3.5 w-3.5 animate-bounce" /> Scroll
        </button>
      </div>
    </section>
  );
}
